'use client';
import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';

import { callService } from '@api/remote-call';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { FlexibleDiv } from '@components/formStyles';
import ErrorPage404 from '@components/views/error/ErrorPage404';
import Markdown from '@components/utils/Markdown';
import Typography from '@components/Typography';

import {
  Wrapper,
  BannerWrapper,
  Banner,
  TextContentWrapper,
  Title,
  ConditionsDot,
  ConditionsItem,
  ConditionsText,
  Label,
} from './styled';
import PaymentOptionDetailSkeleton from './PaymentOptionDetailSkeleton';

const PaymentOptionDetail = () => {
  const params = useParams();

  //============================
  // Fetch the payment options
  //============================
  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery(
    ['paymentOptions'],
    () =>
      callService(
        {
          service: 'ContentsService',
          method: 'getPaymentDetails',
          version: 'v3',
        },
        {
          slug: params?.slug as string,
        }
      ),
    { enabled: Boolean(params?.slug) }
  );

  //============================
  // Creating the bread items
  //============================
  const breadItems = useMemo(() => {
    const itemTitle = data?.imageAndTitle?.[0]?.name;
    return [
      {
        title: 'Ana Sayfa',
        url: '/',
      },
      {
        title: 'Ödeme Seçenekleri',
        url: '/odeme-secenekleri',
      },
      {
        title: itemTitle,
        url: `/${params?.slug}`,
      },
    ];
  }, [params, data]);

  //=====================================================
  // Extracting the label and description and the list items from the one text
  //=====================================================
  const description = useMemo(() => {
    const text = data?.description?.[0]?.text?.split('__Kampanya Koşulları__');

    return [text?.[0], text?.[1]?.replace(/\n/g, '')];
  }, [data]);

  if (isError) return <ErrorPage404 />;

  return (
    <div>
      {isLoading ? (
        <PaymentOptionDetailSkeleton />
      ) : (
        <Wrapper>
          <BannerWrapper>
            <Banner
              src={data?.imageAndTitle?.[0]?.images?.[0]?.url}
              alt={data?.imageAndTitle?.[0]?.images?.[0]?.alt}
              fill
              priority
            />
          </BannerWrapper>

          <TextContentWrapper>
            <FlexibleDiv top={40} bottom={18}>
              <Breadcrumb items={breadItems} />
            </FlexibleDiv>

            <Title>{data?.imageAndTitle?.[0]?.name}</Title>

            <Markdown text={description?.[0] || ''} />
            {description?.[1] && description?.[1]?.length > 0 ? (
              <>
                <Label>
                  <Typography variant='Title4' weight='SemiBold' color='D400'>
                    Kampanya Koşulları
                  </Typography>
                </Label>
                <FlexibleDiv bottom={60}>
                  {description[1]
                    ?.split('- ')
                    .filter((item: string) => item !== '')
                    .map((item: string) => (
                      <ConditionsItem key={item}>
                        <ConditionsDot />
                        <ConditionsText>
                          <Markdown text={item} />
                        </ConditionsText>
                      </ConditionsItem>
                    ))}
                </FlexibleDiv>
              </>
            ) : null}
          </TextContentWrapper>
        </Wrapper>
      )}
    </div>
  );
};

export default PaymentOptionDetail;
